import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getExpenses, createExpense, deleteExpense } from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { formatCurrency, formatDate, toLocalDateString, getCategoryLabel, formatDualCurrency } from '../utils/formatters';

const Expenses = () => {
  const { stores, getStoreName } = useStores();
  const { exchangeRate } = useExchangeRate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    concept: '',
    amount: '',
    currency: 'USD',
    category: '',
    date: new Date(),
    description: '',
    store_id: '',
    payment_reference: '',
    payment_reference2: '',
    payment_reference3: '',
    payment_reference4: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    storeId: '',
    startDate: null,
    endDate: null,
    reference: '',
  });

  const expenseCategories = [
    { value: 'operativo', label: 'Operativos' },
    { value: 'personal', label: 'Personal' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'mantenimiento', label: 'Mantenimiento' },
    { value: 'seguros', label: 'Seguros' },
    { value: 'servicios', label: 'Servicios' },
    { value: 'sueldos_directivos', label: 'Sueldos a directivos' },
    { value: 'compras_inventario', label: 'Compras de inventario' },
    { value: 'pagos_proveedores', label: 'Pagos a proveedores' },
    { value: 'creditos_bancarios', label: 'Créditos bancarios' },
    { value: 'comisiones_bancarias', label: 'Comisiones bancarias' },
    { value: 'notas_debito', label: 'Notas de débito' },
    { value: 'otros', label: 'Otros' },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const expensesData = await getExpenses(filters);
      setExpenses(expensesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [filters]);

  const handleOpenModal = () => {
    setFormData({
      concept: '',
      amount: '',
      currency: 'USD',
      category: '',
      date: new Date(),
      description: '',
      store_id: '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      concept: '',
      amount: '',
      currency: 'USD',
      category: '',
      date: new Date(),
      description: '',
      store_id: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const amount = parseFloat(formData.amount);
      const currentExchangeRate = exchangeRate || 1;
      
      // Calculate amounts in both currencies
      let amount_usd, amount_ves;
      if (formData.currency === 'USD') {
        amount_usd = amount;
        amount_ves = amount * currentExchangeRate;
      } else if (formData.currency === 'VES_EFECTIVO' || formData.currency === 'VES_TRANSFERENCIA') {
        amount_ves = amount;
        amount_usd = amount / currentExchangeRate;
      } else {
        // Fallback for old VES entries
        amount_ves = amount;
        amount_usd = amount / currentExchangeRate;
      }

      const expenseData = {
        concept: formData.concept,
        amount_usd: parseFloat(amount_usd.toFixed(2)),
        amount_ves: parseFloat(amount_ves.toFixed(2)),
        exchange_rate_used: currentExchangeRate,
        currency: formData.currency === 'VES_EFECTIVO' || formData.currency === 'VES_TRANSFERENCIA' ? 'VES' : formData.currency,
        category: formData.category,
        date: toLocalDateString(formData.date),
        description: formData.description || null,
        store_id: formData.store_id || null,
        payment_method: formData.currency === 'VES_EFECTIVO' ? 'bs_efectivo' : 
                       formData.currency === 'VES_TRANSFERENCIA' ? 'bs_transferencia' : 'efectivo',
        payment_reference: formData.payment_reference || null,
        payment_reference2: formData.payment_reference2 || null,
        payment_reference3: formData.payment_reference3 || null,
        payment_reference4: formData.payment_reference4 || null,
      };

      await createExpense(expenseData);
      await fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('Error creating expense:', err);
      setError('Error al registrar el gasto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este gasto?')) {
      return;
    }

    try {
      await deleteExpense(expenseId);
      await fetchData();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError('Error al eliminar el gasto');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      storeId: '',
      startDate: null,
      endDate: null,
      reference: '',
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      operativo: 'bg-blue-100 text-blue-800',
      personal: 'bg-green-100 text-green-800',
      marketing: 'bg-purple-100 text-purple-800',
      mantenimiento: 'bg-yellow-100 text-yellow-800',
      seguros: 'bg-red-100 text-red-800',
      servicios: 'bg-indigo-100 text-indigo-800',
      sueldos_directivos: 'bg-pink-100 text-pink-800',
      compras_inventario: 'bg-orange-100 text-orange-800',
      pagos_proveedores: 'bg-amber-100 text-amber-800',
      creditos_bancarios: 'bg-teal-100 text-teal-800',
      notas_debito: 'bg-orange-100 text-orange-800',
      otros: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const totalExpenses = expenses.reduce((sum, expense) => sum + (expense.amount_usd || 0), 0);

  // Group expenses by category for summary
  const expensesByCategory = expenses.reduce((acc, expense) => {
    if (!acc[expense.category]) {
      acc[expense.category] = { total: 0, count: 0 };
    }
    acc[expense.category].total += (expense.amount_usd || 0);
    acc[expense.category].count += 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando gastos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Registro de Gastos</h1>
          <p className="text-gray-600 mt-1">
            {expenses.length} gastos registrados • Total: {formatCurrency(totalExpenses)}
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchData} className="btn-outline">
            🔄 Actualizar
          </button>
          <button onClick={handleOpenModal} className="btn-primary">
            ➕ Registrar Gasto
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Summary by Category */}
      {Object.keys(expensesByCategory).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(expensesByCategory).map(([category, data]) => (
            <div key={category} className="card text-center">
              <div className={`badge ${getCategoryColor(category)} mb-2`}>
                {getCategoryLabel(category)}
              </div>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(data.total)}
              </p>
              <p className="text-sm text-gray-600">{data.count} gastos</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="form-label">Categoría</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="form-select"
            >
              <option value="">Todas las categorías</option>
              {expenseCategories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Tienda</label>
            <select
              value={filters.storeId}
              onChange={(e) => handleFilterChange('storeId', e.target.value)}
              className="form-select"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              className="form-input"
              placeholderText="Seleccionar fecha"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              className="form-input"
              placeholderText="Seleccionar fecha"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Referencia</label>
            <input
              type="text"
              placeholder="Buscar por referencia..."
              value={filters.reference}
              onChange={(e) => handleFilterChange('reference', e.target.value)}
              className="form-input"
            />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} className="btn-outline w-full">
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      {expenses.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {Object.values(filters).some(f => f) ? 'No se encontraron gastos con estos filtros' : 'No hay gastos registrados'}
          </h3>
          <p className="text-gray-600 mb-4">
            {Object.values(filters).some(f => f) ? 'Intenta ajustar los filtros de búsqueda' : 'Comienza registrando tu primer gasto'}
          </p>
          {!Object.values(filters).some(f => f) && (
            <button onClick={handleOpenModal} className="btn-primary">
              Registrar Primer Gasto
            </button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Categoría</th>
                  <th>Tienda</th>
                  <th>Monto</th>
                  <th>Descripción</th>
                  <th>Usuario</th>
                  <th>Referencia</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>{formatDate(expense.date)}</td>
                    <td>
                      <span className="font-medium text-gray-900">
                        {expense.concept}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getCategoryColor(expense.category)}`}>
                        {getCategoryLabel(expense.category)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-600">
                          {expense.store_id ? '🏪' : '🏢'}
                        </span>
                        <span>
                          {expense.store_id ? getStoreName(expense.store_id) : 'Gasto general'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="font-semibold text-red-600">
                        {expense.amount_usd ? (
                          <div>
                            <div>${expense.amount_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-xs text-gray-500">
                              Bs. {expense.amount_ves?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                            </div>
                          </div>
                        ) : (
                          <span>{formatCurrency(expense.amount_usd || 0)}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="text-gray-600">
                        {expense.description || 'Sin descripción'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {expense.user_name || 'Sistema'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {expense.payment_reference || '-'}
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        title="Eliminar gasto"
                      >
                        🗑️ Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Registrar Nuevo Gasto</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Concepto *</label>
                  <input
                    type="text"
                    name="concept"
                    value={formData.concept}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: Alquiler local, Publicidad Facebook, etc."
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Categoría *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Seleccionar categoría</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Monto *</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                      >
                        <option value="USD">USD ($)</option>
                        <option value="VES_EFECTIVO">BS EFECTIVO</option>
                        <option value="VES_TRANSFERENCIA">BS TRANSFERENCIA</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder={formData.currency === 'USD' ? '100.00' : '3500.00'}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  {formData.amount && exchangeRate && (
                    <div className="mt-1 text-sm text-gray-600">
                      {formData.currency === 'USD' ? (
                        <span>≈ Bs. {(parseFloat(formData.amount || 0) * exchangeRate).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : (
                        <span>≈ ${(parseFloat(formData.amount || 0) / exchangeRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <DatePicker
                    selected={formData.date}
                    onChange={(date) => setFormData(prev => ({ ...prev, date }))}
                    className="form-input"
                    dateFormat="dd/MM/yyyy"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tienda (Opcional)</label>
                  <select
                    name="store_id"
                    value={formData.store_id}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Gasto general (no específico de tienda)</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows="3"
                    placeholder="Descripción adicional del gasto..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Referencia de pago 1 (para conciliación bancaria)</label>
                  <input
                    type="text"
                    name="payment_reference"
                    value={formData.payment_reference}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: 0000123456"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <label className="form-label">Referencia 2</label>
                    <input type="text" name="payment_reference2" value={formData.payment_reference2} onChange={handleInputChange} className="form-input" placeholder="Opcional" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia 3</label>
                    <input type="text" name="payment_reference3" value={formData.payment_reference3} onChange={handleInputChange} className="form-input" placeholder="Opcional" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia 4</label>
                    <input type="text" name="payment_reference4" value={formData.payment_reference4} onChange={handleInputChange} className="form-input" placeholder="Opcional" />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Registrando...
                    </span>
                  ) : (
                    'Registrar Gasto'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;